import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LineStatus } from '@contracts/signals';
import { useMe, useMyPreferences } from '../hooks/useMe';
import { api, ApiError } from '../lib/api';
import { dateTime } from '../lib/format';

/** The signed-in user's own LINE opt-in (only when Login with LINE is enabled). */
function MyLinePreferences() {
  const me = useMe();
  const prefs = useMyPreferences();
  const user = me.data?.user;
  if (!me.data?.authRequired || !user) return null;
  return (
    <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
      <span className="small">Send to my LINE ({user.displayName}):</span>
      <label className="small row" style={{ gap: 4 }}>
        <input
          type="checkbox"
          checked={user.notifyAlerts}
          disabled={prefs.isPending}
          onChange={(e) => prefs.mutate({ notifyAlerts: e.target.checked })}
        />
        BUY/SELL signals
      </label>
      <label className="small row" style={{ gap: 4 }}>
        <input
          type="checkbox"
          checked={user.notifyOutcomes}
          disabled={prefs.isPending}
          onChange={(e) => prefs.mutate({ notifyOutcomes: e.target.checked })}
        />
        TP/SL results
      </label>
      {user.isFriend === false ? (
        <span className="badge badge--warn">Add the LINE Official Account as a friend</span>
      ) : null}
    </div>
  );
}

/** LINE push status and a test button. Credentials live only in the backend .env. */
export function LinePanel() {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['line-status'],
    queryFn: () => api.get<LineStatus>('/api/v1/notifications/line'),
    refetchInterval: 30_000,
  });
  const test = useMutation({
    mutationFn: () => api.post<LineStatus & { sent: boolean }>('/api/v1/notifications/line/test'),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['line-status'] }),
  });
  const s = status.data;
  if (status.isError) return <p className="small muted">LINE status unavailable.</p>;
  if (!s) return <p className="small muted">Loading LINE status…</p>;
  return (
    <div className="stack" style={{ gap: 6 }}>
      <MyLinePreferences />
      <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
        {s.configured ? (
          <span className="badge badge--live">
            <span aria-hidden="true">✓</span> LINE connected
          </span>
        ) : (
          <span className="badge badge--neutral">LINE not set up</span>
        )}
        <button type="button" className="btn" disabled={!s.configured || test.isPending} onClick={() => test.mutate()}>
          {test.isPending ? 'Sending…' : 'Send LINE test message'}
        </button>
      </div>
      {s.configured ? (
        <p className="small muted" style={{ margin: 0 }}>
          Sent {s.sent} · failed {s.failed}
          {s.lastSentAt ? ` · last ${dateTime(s.lastSentAt)}` : ''} · results {s.sendOutcomes ? 'on' : 'off'} · replay
          alerts {s.sendReplay ? 'on' : 'off'}
        </p>
      ) : (
        <p className="small muted" style={{ margin: 0 }}>
          Set LINE_CHANNEL_ACCESS_TOKEN in the backend .env and restart the API (see .env.example).
        </p>
      )}
      {test.isSuccess ? (
        <p className="small pos" role="status" style={{ margin: 0 }}>
          Test message sent — check LINE.
        </p>
      ) : null}
      {test.error ? (
        <p className="small neg" role="alert" style={{ margin: 0 }}>
          {test.error instanceof ApiError ? test.error.message : 'Test failed.'}
        </p>
      ) : s.lastError ? (
        <p className="small neg" style={{ margin: 0 }}>
          Last error: {s.lastError}
        </p>
      ) : null}
    </div>
  );
}
